'use client';

import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Bars3Icon, ListBulletIcon, PlusIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import Link from '@/components/atoms/link';
import Breadcrumb from '@/components/molecules/breadcrumb';
import PurchaseListItem from '@/components/molecules/purchase-list-item';
import Confirmation from '@/components/organisms/confirmation';
import toast from '@/components/atoms/toaster/helpers/toast';
import useEntityToasters from '@/hooks/useEntityToasters';
import { resolveAddToCartAvailability } from '@/utils/product/add-to-cart-availability';
import { isBackorderRestricted } from '@/utils/isBackorderRestricted';
import ListSwitcher from './components/list-switcher';
import ListMenu from './components/list-menu';
import CreateListDrawer from './components/create-list-drawer';
import RenameListDrawer from './components/rename-list-drawer';
import MoveItemDrawer from './components/move-item-drawer';
import { DashboardLinks } from '../../constants';
import { PurchaseListDetailPageProps } from './types';
import type { PurchaseListItem as PurchaseListItemType } from '@/types/entity/purchase-list';

const PurchaseListDetailPage = ({
  purchaseList,
  allLists,
  browseProductsHref = '/',
  permissions,
  accountId,
  backOrdersAllowed,
  paymentTerms,
  onUpdatePurchaseList,
  onDeletePurchaseList,
  onAddPurchaseListToCart,
  onAddItemToCart,
  onRemoveItem,
  onCreateList,
  onMoveItem,
  onSelectList,
  onSetDefault,
}: PurchaseListDetailPageProps) => {
  const translate = useTranslations();
  const translateDashboard = useTranslations('dashboard');

  const { showDeletedMessage, showDeletedFailedMessage, showSavedMessage, showFailedMessage } =
    useEntityToasters('purchaselist');

  const [viewMode, setViewMode] = useState<'image' | 'list'>('image');
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [renameDrawerOpen, setRenameDrawerOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<PurchaseListItemType | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addingAll, setAddingAll] = useState(false);

  // EWM-554 — per-row quantity is ephemeral "how many to add to cart" selection,
  // owned here and initialised to 0 for every row (commercetools can't persist qty 0,
  // see EWM-384). Reset when switching lists so a fresh list starts all-zero.
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  // EWM-760 (was a setState-in-effect on `[purchaseList?.id]`) — adjust-
  // state-during-render: reset when switching lists so a fresh list starts
  // all-zero. `prevPurchaseListId` is compared exactly like the old effect's
  // dependency array.
  const [prevPurchaseListId, setPrevPurchaseListId] = useState(purchaseList?.id);
  if (prevPurchaseListId !== purchaseList?.id) {
    setPrevPurchaseListId(purchaseList?.id);
    setSelectedQuantities({});
  }
  const qtyOf = (id: string) => selectedQuantities[id] ?? 0;

  const isOwner = purchaseList?.account?.accountId === accountId;
  const canEdit = isOwner ? permissions?.UpdateMyShoppingLists : permissions?.UpdateOthersShoppingLists;
  const canDelete = isOwner ? permissions?.DeleteMyShoppingLists : permissions?.DeleteOthersShoppingLists;
  const canUpdateCart = permissions?.UpdateMyCarts && permissions?.CreateMyCarts;

  const listSummaries = allLists.map((l) => ({
    id: l.id,
    name: l.name,
    itemCount: l.items.length,
    isDefault: l.isDefault ?? false,
  }));
  const destinationLists = listSummaries.filter((l) => l.id !== purchaseList?.id);

  // EWM-299 (OQ-5) — names for the drawers' inline duplicate check. Create sees
  // every list; rename excludes the list being edited so keeping its own name
  // is allowed. The BFF enforces the same rule.
  const allListNames = allLists.map((l) => l.name);
  const otherListNames = allLists.filter((l) => l.id !== purchaseList?.id).map((l) => l.name);

  if (!purchaseList) {
    return (
      <div className="mx-auto w-full max-w-7xl pt-8 pb-12">
        {/* EWM-564 — breadcrumbs, consistent with the rest of the site */}
        <nav aria-label={translate('common.breadcrumb')} className="mb-4">
          <Breadcrumb>
            <Link href="/" underlineOnHover={false}>
              {translate('common.home')}
            </Link>
            <span aria-current="page">{translate('common.purchase-lists')}</span>
          </Breadcrumb>
        </nav>
        <h1 className="text-32 text-primary mb-8 font-bold">{translate('common.purchase-lists')}</h1>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-8 py-16 text-center">
          <p className="text-18 text-primary font-semibold">{translate('dashboard.purchase-list-no-items')}</p>
          <p className="text-14 mt-2 text-gray-500">{translate('dashboard.purchase-list-empty-hint')}</p>
          <button
            type="button"
            onClick={() => setCreateDrawerOpen(true)}
            className="bg-primary text-14 hover:bg-primary/90 mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-colors"
          >
            {translate('dashboard.purchase-list-add')}
          </button>
        </div>
        <CreateListDrawer
          isOpen={createDrawerOpen}
          onClose={() => setCreateDrawerOpen(false)}
          existingNames={allListNames}
          onSubmit={async (name, description) => {
            const ok = (await onCreateList?.(name, description)) ?? false;
            if (ok) showSavedMessage();
            else showFailedMessage();
            return ok;
          }}
        />
      </div>
    );
  }

  const items = purchaseList.items;
  const itemsCount = items.length;

  // EWM-554 — rows start at 0; "Add all to cart" stays unavailable until the user
  // toggles at least one quantity up.
  const hasSelectedQuantities = items.some((item) => qtyOf(item.id) > 0);

  // EWM-1090 — "Add All" applies the SAME availability rule as the row + the
  // PDP/PLP (`resolveAddToCartAvailability`, EWM-360), fed the customer's cash-aware
  // back-order entitlement (`isBackorderRestricted`, EWM-641 — matches the uploader
  // and the authoritative checkout gate). Mirroring `resolveUploadLineAvailability`
  // (EWM-944): skip lines that are not addable, add the rest, and toast a general
  // message if anything was dropped. This removes the row-vs-Add-All contradiction
  // without touching the authoritative checkout gate.
  const entitled = !isBackorderRestricted({ backOrdersAllowed, paymentTerms });
  const isAddable = (item: PurchaseListItemType) =>
    !resolveAddToCartAvailability({
      inStock: item.inStock,
      priceUnavailable: item.priceUnavailable,
      removedFromSale: item.removedFromSale,
      backOrdersAllowed: entitled,
    }).disabled;

  const handleAddAll = async () => {
    const selected = items.filter((item) => qtyOf(item.id) > 0);
    const addable = selected.filter(isAddable);
    const skipped = selected.length - addable.length;
    const payload = addable.map((item) => ({ sku: item.sku, count: qtyOf(item.id) }));
    if (payload.length) {
      setAddingAll(true);
      const ok = await onAddPurchaseListToCart?.(payload);
      setAddingAll(false);
      if (!ok) showFailedMessage();
    }
    // Tell the user why some selected rows didn't make it into the cart — whether
    // or not anything was added (all-unavailable keeps the existing empty-guard,
    // no request, but still explains the outcome).
    if (skipped > 0) {
      toast.error(translateDashboard('purchase-list-add-all-skipped'), { position: 'top-right' });
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl pt-8 pb-12">
      {/* EWM-564 — breadcrumbs, consistent with the rest of the site */}
      <nav aria-label={translate('common.breadcrumb')} className="mb-4">
        <Breadcrumb>
          <Link href="/" underlineOnHover={false}>
            {translate('common.home')}
          </Link>
          <Link href={DashboardLinks.shoppingLists} underlineOnHover={false}>
            {translate('common.purchase-lists')}
          </Link>
          <span aria-current="page">{purchaseList.name}</span>
        </Breadcrumb>
      </nav>

      {/* Header — title+description left, actions right (Dan's justify-between row) */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-32 text-primary font-bold">{purchaseList.name}</h1>
            {purchaseList.isDefault && (
              <span className="text-11 inline-block rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-700">
                {translateDashboard('purchase-list-default-badge')}
              </span>
            )}
          </div>
          {purchaseList.description && <p className="text-15 mt-1 text-gray-500">{purchaseList.description}</p>}
        </div>

        <div className="flex items-center gap-2">
          {listSummaries.length > 1 && (
            <ListSwitcher lists={listSummaries} activeId={purchaseList.id} onSelect={(id) => onSelectList?.(id)} />
          )}

          <button
            type="button"
            onClick={() => setCreateDrawerOpen(true)}
            className="bg-primary text-14 hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-white transition-colors"
          >
            <PlusIcon aria-hidden className="h-4 w-4" />
            {translate('dashboard.purchase-list-add')}
          </button>

          {(canEdit || canDelete) && (
            <ListMenu
              onRename={() => setRenameDrawerOpen(true)}
              onDelete={() => setDeleteConfirmOpen(true)}
              onSetDefault={
                onSetDefault
                  ? async () => {
                      const ok = (await onSetDefault()) ?? false;
                      if (ok) showSavedMessage();
                      else showFailedMessage();
                    }
                  : undefined
              }
              isDefault={purchaseList.isDefault}
            />
          )}
        </div>
      </div>

      {/* Toolbar row — view toggle left, Add All right */}
      {itemsCount > 0 && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-1">
            <button
              type="button"
              aria-label={translate('dashboard.purchase-list-view-image')}
              onClick={() => setViewMode('image')}
              className={[
                'rounded-md p-2 transition-colors',
                viewMode === 'image' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100',
              ].join(' ')}
            >
              <ListBulletIcon aria-hidden className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={translate('dashboard.purchase-list-view-list')}
              onClick={() => setViewMode('list')}
              className={[
                'rounded-md p-2 transition-colors',
                viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100',
              ].join(' ')}
            >
              <Bars3Icon aria-hidden className="h-4 w-4" />
            </button>
          </div>

          {canUpdateCart && (
            <button
              type="button"
              onClick={handleAddAll}
              disabled={!itemsCount || addingAll || !hasSelectedQuantities}
              className="bg-primary text-14 hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShoppingCartIcon aria-hidden className="h-4 w-4" />
              {translate('dashboard.purchase-list-add-all')}
            </button>
          )}
        </div>
      )}

      {/* Items list */}
      <div className="flex flex-col gap-3 pb-12">
        {itemsCount === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-8 py-16 text-center">
            <p className="text-18 text-primary font-semibold">{translate('dashboard.purchase-list-no-items')}</p>
            <p className="text-14 mt-2 text-gray-500">{translate('dashboard.purchase-list-empty-hint')}</p>
            <Link
              href={browseProductsHref}
              underlineOnHover={false}
              className="bg-primary text-14 hover:bg-primary/90 mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-colors"
            >
              {translate('cart.browse-products')}
            </Link>
          </div>
        ) : (
          items.map((item) => {
            const selectedQty = qtyOf(item.id);
            return (
              <PurchaseListItem
                key={item.id}
                item={{ ...item, quantity: selectedQty }}
                viewMode={viewMode}
                backOrdersAllowed={backOrdersAllowed}
                paymentTerms={paymentTerms}
                onRemove={async () => !!onRemoveItem?.(item.id)}
                onAddToCart={async () => {
                  const ok = !!(await onAddItemToCart?.({ ...item, quantity: selectedQty }));
                  if (ok) toast.success(translate('cart.item-added'), { position: 'top-right' });
                  else showFailedMessage();
                  return ok;
                }}
                onQuantityChange={(quantity) => setSelectedQuantities((prev) => ({ ...prev, [item.id]: quantity }))}
                onMove={() => setMoveTarget(item)}
              />
            );
          })
        )}
      </div>

      {/* Bottom Add All */}
      {itemsCount > 0 && canUpdateCart && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleAddAll}
            disabled={!itemsCount || addingAll || !hasSelectedQuantities}
            className="bg-primary text-14 hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ShoppingCartIcon aria-hidden className="h-4 w-4" />
            {translate('dashboard.purchase-list-add-all')}
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      <Confirmation
        translations={{
          title: translate('dashboard.purchase-list-delete'),
          summary: translate('dashboard.purchase-list-delete-confirm'),
          cancel: translate('common.cancel'),
          confirm: translate('common.delete'),
        }}
        isOpen={deleteConfirmOpen}
        onConfirm={async () => {
          const ok = (await onDeletePurchaseList?.()) ?? false;
          if (ok) showDeletedMessage();
          else showDeletedFailedMessage();
          setDeleteConfirmOpen(false);
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      {/* Drawers */}
      <CreateListDrawer
        isOpen={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        existingNames={allListNames}
        onSubmit={async (name, description) => {
          const ok = (await onCreateList?.(name, description)) ?? false;
          if (ok) showSavedMessage();
          else showFailedMessage();
          return ok;
        }}
      />

      <RenameListDrawer
        isOpen={renameDrawerOpen}
        onClose={() => setRenameDrawerOpen(false)}
        currentName={purchaseList.name}
        currentDescription={purchaseList.description}
        existingNames={otherListNames}
        onSubmit={async (name, description) => {
          const ok = (await onUpdatePurchaseList?.({ id: purchaseList.id, name, description })) ?? false;
          if (ok) showSavedMessage();
          else showFailedMessage();
          return ok;
        }}
      />

      <MoveItemDrawer
        isOpen={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        item={moveTarget}
        destinationLists={destinationLists}
        onSubmit={async (targetListId) => {
          if (!moveTarget) return false;
          const ok = (await onMoveItem?.(moveTarget, targetListId)) ?? false;
          if (!ok) showFailedMessage();
          return ok;
        }}
      />
    </div>
  );
};

export default PurchaseListDetailPage;
